import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import dropboxService, { DropboxBackupSettings } from '../services/dropboxService';

const DropboxSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<DropboxBackupSettings>({
    enabled: false,
    autoUpload: false,
    autoDelete: false,
    maxBackups: 10,
    folderName: 'MaintenanceSystemBackups',
    accessToken: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);
  const [isDropboxConnected, setIsDropboxConnected] = useState<boolean>(false);
  
  // حالة الإشعارات
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // تحميل الإعدادات عند تحميل الصفحة
  useEffect(() => {
    loadSettings();
    checkDropboxConnection();
  }, []);
  
  // تحميل إعدادات Dropbox
  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await dropboxService.getDropboxBackupSettings();
      setSettings(settings);
    } catch (error) {
      console.error('Error loading Dropbox settings:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحميل إعدادات Dropbox',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // التحقق من اتصال Dropbox
  const checkDropboxConnection = async () => {
    try {
      const settings = await dropboxService.getDropboxBackupSettings();
      setIsDropboxConnected(!!settings.accessToken);
    } catch (error) {
      console.error('Error checking Dropbox connection:', error);
      setIsDropboxConnected(false);
    }
  };
  
  // تحديث الإعدادات
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // حفظ الإعدادات
  const handleSave = async () => {
    try {
      setSaving(true);
      await dropboxService.saveDropboxBackupSettings(settings);
      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات Dropbox بنجاح',
        severity: 'success'
      });
      await checkDropboxConnection();
    } catch (error) {
      console.error('Error saving Dropbox settings:', error);
      setSnackbar({
        open: true,
        message: 'فشل في حفظ إعدادات Dropbox',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };
  
  // اختبار الاتصال بـ Dropbox
  const handleTestConnection = async () => {
    try {
      setTesting(true);
      
      // التحقق من وجود رمز الوصول
      if (!settings.accessToken) {
        throw new Error('يرجى إدخال رمز الوصول أولاً');
      }
      
      // اختبار الاتصال
      await dropboxService.getDropboxBackups();
      
      setSnackbar({
        open: true,
        message: 'تم الاتصال بـ Dropbox بنجاح',
        severity: 'success'
      });
      
      setIsDropboxConnected(true);
    } catch (error) {
      console.error('Error testing Dropbox connection:', error);
      setSnackbar({
        open: true,
        message: 'فشل في الاتصال بـ Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'),
        severity: 'error'
      });
      setIsDropboxConnected(false);
    } finally {
      setTesting(false);
    }
  };
  
  // إغلاق الإشعار
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SettingsIcon sx={{ mr: 1 }} />
          <Typography variant="h5" component="h1">
            إعدادات Dropbox
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                للحصول على رمز الوصول، قم بزيارة{' '}
                <a
                  href="https://www.dropbox.com/developers/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  Dropbox Developers
                </a>
                {' '}وقم بإنشاء تطبيق جديد. ثم انسخ رمز الوصول من إعدادات التطبيق.
              </Typography>
            </Alert>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="رمز الوصول"
              name="accessToken"
              value={settings.accessToken}
              onChange={handleChange}
              type="password"
              helperText="رمز الوصول الخاص بتطبيق Dropbox"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="اسم المجلد"
              name="folderName"
              value={settings.folderName}
              onChange={handleChange}
              helperText="اسم المجلد الذي سيتم حفظ النسخ الاحتياطية فيه"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="الحد الأقصى للنسخ الاحتياطية"
              name="maxBackups"
              type="number"
              value={settings.maxBackups}
              onChange={handleChange}
              helperText="الحد الأقصى لعدد النسخ الاحتياطية المحفوظة"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={handleChange}
                  name="enabled"
                />
              }
              label="تفعيل النسخ الاحتياطي في Dropbox"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoUpload}
                  onChange={handleChange}
                  name="autoUpload"
                />
              }
              label="التحميل التلقائي للنسخ الاحتياطية"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoDelete}
                  onChange={handleChange}
                  name="autoDelete"
                />
              }
              label="حذف النسخ الاحتياطية القديمة تلقائياً"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={isDropboxConnected ? <CloudDoneIcon /> : <CloudOffIcon />}
                onClick={handleTestConnection}
                disabled={testing || !settings.accessToken}
              >
                {testing ? 'جاري الاختبار...' : (isDropboxConnected ? 'متصل' : 'اختبار الاتصال')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DropboxSettingsPage; 